import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Box,
  Typography,
  FormHelperText,
  Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useCreateService, useUpdateService } from '../../hooks/useServices';
import { Service } from '../../services/services';
import CurrencyInput from '../common/CurrencyInput';

const createServiceSchema = z.object({
  name: z.string().min(1, 'Service name is required').max(200),
  shortDescription: z.string().max(500).optional(),
  description: z.string().max(5000).optional(),
  duration: z.number().min(5, 'Minimum 5 minutes').max(1440, 'Maximum 24 hours'),
  pricingType: z.enum(['fixed', 'hourly', 'custom', 'free']),
  price: z.number().min(0, 'Price cannot be negative'),
  category: z.enum(['coaching', 'consulting', 'strategy', 'training', 'workshop', 'admin', 'social_media', 'content', 'design', 'other']),
  meetingType: z.enum(['online', 'in_person', 'phone', 'flexible']),
  location: z.string().optional(),
  onlineMeetingUrl: z.string().optional(),
  color: z.string().optional(),
  allowClientBooking: z.boolean(),
  requireApproval: z.boolean(),
  status: z.enum(['active', 'inactive']),
});

type CreateServiceFormData = z.infer<typeof createServiceSchema>;

interface CreateServiceModalProps {
  open: boolean;
  onClose: () => void;
  service?: Service | null;
}

const categoryOptions = [
  { value: 'coaching', label: 'Coaching' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'training', label: 'Training' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'admin', label: 'Admin' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'content', label: 'Content' },
  { value: 'design', label: 'Design' },
  { value: 'other', label: 'Other' },
];

const durationOptions = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
  { value: 300, label: '5 hours' },
  { value: 360, label: '6 hours' },
  { value: 420, label: '7 hours' },
  { value: 480, label: '8 hours' },
  { value: 600, label: '10 hours' },
  { value: 720, label: '12 hours' },
  { value: 960, label: '16 hours' },
  { value: 1200, label: '20 hours' },
  { value: 1440, label: '1 day' },
];

const colorOptions = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

const getDefaultValues = (service?: Service | null): CreateServiceFormData => {
  if (service) {
    return {
      name: service.name,
      shortDescription: service.shortDescription || '',
      description: service.description || '',
      duration: service.duration,
      pricingType: service.pricingType,
      price: service.price / 100, // Convert cents to dollars
      category: service.category as CreateServiceFormData['category'],
      meetingType: service.meetingType,
      location: service.location || '',
      onlineMeetingUrl: service.onlineMeetingUrl || '',
      color: service.color || '#3B82F6',
      allowClientBooking: service.allowClientBooking,
      requireApproval: service.requireApproval,
      status: service.status === 'archived' ? 'inactive' : service.status,
    };
  }
  return {
    name: '',
    shortDescription: '',
    description: '',
    status: 'active',
    pricingType: 'fixed',
    category: 'coaching',
    meetingType: 'online',
    duration: 60,
    price: 0,
    color: '#3B82F6',
    location: '',
    onlineMeetingUrl: '',
    allowClientBooking: true,
    requireApproval: false,
  };
};

export default function CreateServiceModal({ open, onClose, service }: CreateServiceModalProps) {
  const [error, setError] = useState<string | null>(null);
  const createService = useCreateService();
  const updateService = useUpdateService();
  const isEditing = !!service;

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateServiceFormData>({
    resolver: zodResolver(createServiceSchema),
    defaultValues: getDefaultValues(service),
  });

  // Reset form when service changes (opening modal with different service)
  useEffect(() => {
    if (open) {
      reset(getDefaultValues(service));
    }
  }, [service, open, reset]);

  const pricingType = watch('pricingType');
  const meetingType = watch('meetingType');

  const onSubmit = async (data: CreateServiceFormData) => {
    setError(null);

    try {
      // Convert price from dollars to cents
      const priceInCents = Math.round(data.price * 100);

      if (isEditing && service) {
        await updateService.mutateAsync({
          id: service._id,
          data: {
            name: data.name,
            shortDescription: data.shortDescription,
            description: data.description,
            duration: data.duration,
            pricingType: data.pricingType,
            price: priceInCents,
            category: data.category,
            meetingType: data.meetingType,
            location: data.location,
            onlineMeetingUrl: data.onlineMeetingUrl,
            color: data.color,
            allowClientBooking: data.allowClientBooking,
            requireApproval: data.requireApproval,
            status: data.status,
          },
        });
      } else {
        await createService.mutateAsync({
          name: data.name,
          shortDescription: data.shortDescription,
          description: data.description,
          duration: data.duration,
          pricingType: data.pricingType,
          price: priceInCents,
          category: data.category,
          meetingType: data.meetingType,
          location: data.location,
          onlineMeetingUrl: data.onlineMeetingUrl,
          color: data.color,
          allowClientBooking: data.allowClientBooking,
          requireApproval: data.requireApproval,
          status: data.status,
        });
      }

      reset(getDefaultValues());
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || `Failed to ${isEditing ? 'update' : 'create'} service`);
    }
  };

  const handleClose = () => {
    reset(getDefaultValues());
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditing ? 'Edit Service' : 'Add New Service'}</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Basic Info Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                {...register('name')}
                label="Service Name"
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name?.message}
                placeholder="e.g., Business Strategy Session"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Color</InputLabel>
                    <Select {...field} label="Color">
                      {colorOptions.map((color) => (
                        <MenuItem key={color} value={color}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: color,
                              }}
                            />
                            {color}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...register('shortDescription')}
                label="Short Description"
                fullWidth
                multiline
                rows={2}
                error={!!errors.shortDescription}
                helperText={errors.shortDescription?.message || 'Brief description shown in service list'}
                placeholder="A brief overview of what this service includes"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Duration & Pricing
              </Typography>
            </Grid>

            {/* Duration */}
            <Grid item xs={12} md={4}>
              <Controller
                name="duration"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.duration}>
                    <InputLabel>Duration</InputLabel>
                    <Select {...field} label="Duration">
                      {durationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.duration && (
                      <FormHelperText>{errors.duration.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Pricing Type */}
            <Grid item xs={12} md={4}>
              <Controller
                name="pricingType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Pricing Type</InputLabel>
                    <Select {...field} label="Pricing Type">
                      <MenuItem value="fixed">Fixed Price</MenuItem>
                      <MenuItem value="hourly">Hourly Rate</MenuItem>
                      <MenuItem value="custom">Custom/Quote</MenuItem>
                      <MenuItem value="free">Free</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Price */}
            <Grid item xs={12} md={4}>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    value={field.value}
                    onChange={field.onChange}
                    label={pricingType === 'hourly' ? 'Hourly Rate' : 'Price'}
                    fullWidth
                    disabled={pricingType === 'free' || pricingType === 'custom'}
                    error={!!errors.price}
                    helperText={errors.price?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Category & Meeting
              </Typography>
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={6}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select {...field} label="Category">
                      {categoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Meeting Type */}
            <Grid item xs={12} md={6}>
              <Controller
                name="meetingType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Meeting Type</InputLabel>
                    <Select {...field} label="Meeting Type">
                      <MenuItem value="online">Online (Video Call)</MenuItem>
                      <MenuItem value="in_person">In Person</MenuItem>
                      <MenuItem value="phone">Phone Call</MenuItem>
                      <MenuItem value="flexible">Flexible</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Location (for in-person) */}
            {(meetingType === 'in_person' || meetingType === 'flexible') && (
              <Grid item xs={12}>
                <TextField
                  {...register('location')}
                  label="Location"
                  fullWidth
                  placeholder="e.g., 123 Main St, Suite 100, New York, NY"
                  helperText="Physical location for in-person meetings"
                />
              </Grid>
            )}

            {/* Online Meeting URL */}
            {(meetingType === 'online' || meetingType === 'flexible') && (
              <Grid item xs={12}>
                <TextField
                  {...register('onlineMeetingUrl')}
                  label="Meeting URL"
                  fullWidth
                  placeholder="e.g., https://zoom.us/j/123456789"
                  helperText="Default video call link (can be overridden per booking)"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Booking Settings
              </Typography>
            </Grid>

            {/* Booking Settings */}
            <Grid item xs={12} md={4}>
              <Controller
                name="allowClientBooking"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControl fullWidth>
                    <InputLabel>Client Booking</InputLabel>
                    <Select
                      {...field}
                      value={value ? 'yes' : 'no'}
                      onChange={(e) => onChange(e.target.value === 'yes')}
                      label="Client Booking"
                    >
                      <MenuItem value="yes">Allow on Booking Page</MenuItem>
                      <MenuItem value="no">Internal Only</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="requireApproval"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControl fullWidth>
                    <InputLabel>Approval</InputLabel>
                    <Select
                      {...field}
                      value={value ? 'yes' : 'no'}
                      onChange={(e) => onChange(e.target.value === 'yes')}
                      label="Approval"
                    >
                      <MenuItem value="no">Auto-Confirm</MenuItem>
                      <MenuItem value="yes">Require Approval</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Service')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
